import { Box, Button, Typography, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo } from 'react';
import { DrawerHeader } from '../../../../../components/Drawer/DrawerHeader';
import { ExamenesRiesgoResponse } from '../../../consts/periodicosTypes';
import { CircularProgress } from '@mui/material';
import {
    useGetExamenesRiesgoQuery,
    useLazyGetExamenesEmpleadoQuery,
    useLazyGetPdfResultadosExamenEmpleadoQuery
} from '../../../../../services/periodicosApi';
import useReconfirmationExamDrawer from './hooks/useReconfirmationExamDrawer';
import CustomField from '../../../../../components/CustomFormsComponents/CustomField';
import { ReconfirmationExamDrawerFormData } from './hooks/useReconfirmationExamDrawerForm';
import useTranslate from '../../../../../hooks/useTranslate';
import ButtonActions from '../../../../../components/ButtonActions';
import BackdropComponent from '../../../../../components/Backdrop/BackdropComponent';
import { downloadBase64File, getDate } from '../../../../../utils/common';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';

interface PeriodicosExamDrawerProps {
    selectedIds: string[];
    setOpen: (state: string) => void;
    selectedEmployee: any;
}

const PeriodicosReconfirmationExamDrawer = ({
    setOpen,
    selectedEmployee,
    selectedIds
}: PeriodicosExamDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const { translate } = useTranslate(t);
    const idRiesgo = Number(selectedIds[0]?.split('-')[1]);
    const [downloading, setDownloading] = useState(false);
    const [triggerDownloadPdf] = useLazyGetPdfResultadosExamenEmpleadoQuery();

    const handleCloseDrawer = () => {
        setOpen('');
    };

    const { reconfirmationExam, handleSave, isAssigningExam } = useReconfirmationExamDrawer({
        handleCloseDrawer: handleCloseDrawer
    });

    const { fieldsConfig, errors, formData, handleChange } = reconfirmationExam;

    const { data: examsResponse, isLoading } = useGetExamenesRiesgoQuery(idRiesgo, {
        skip: !idRiesgo
    }) as { data?: ExamenesRiesgoResponse; isLoading: boolean };

    const [
        triggerGetExamenesEmpleado,
        { data: examenesEmpleadoData, isFetching: isFetchingExamenesEmpleadoData }
    ] = useLazyGetExamenesEmpleadoQuery();

    const idEmpleado = selectedEmployee?.idEmpleadoNomina;

    const examsData = examsResponse?.examenes || [];

    const empleadoCuil = selectedEmployee?.cuil;

    const renderDownloadButton = (label: string, onClick: () => void) => (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#F5F5F5',
                borderRadius: '6px',
                padding: '8px 12px',
                marginTop: '5px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#EAEAEA' }
            }}
        >
            <Typography variant="body2">{label}</Typography>
            <DownloadOutlinedIcon />
        </Box>
    );

    const handleDownloadPdf = async (idExamenEmpleado: number) => {
        try {
            setDownloading(true);
            const response = await triggerDownloadPdf(idExamenEmpleado).unwrap();

            if (
                typeof response === 'object' &&
                'data' in response &&
                typeof (response as any).data === 'string'
            ) {
                const fileName = `Resultado_Exámen_${empleadoCuil}`;
                const fileUrl = (response as any).data;
                await downloadBase64File(fileUrl, idExamenEmpleado, fileName);
            } else {
                console.error(response);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        if (idEmpleado) {
            triggerGetExamenesEmpleado(idEmpleado)
                .unwrap()
                .then((res) => {})
                .catch((err) => console.error(' Error', err));
        }
    }, [idEmpleado, triggerGetExamenesEmpleado]);

    const examOptions = useMemo(
        () =>
            examsData.map((exam) => ({
                id: exam.examenMedico.id,
                nombre: exam.examenMedico.descripcion,
                frecuencia: exam.examenMedico.frecuencia,
                cantidad: exam.cantidad
            })),
        [examsData]
    );

    const [selectedExams, setSelectedExams] = useState<typeof examOptions>([]);

    useEffect(() => {
        setSelectedExams(examOptions);
    }, [examsResponse]);

    const adaptedExamsData = () => {
        if (!examsData || !selectedEmployee?.examenImportacion?.id) return;
        const previousEmployeesExams = examsData?.filter(
            (employeeExam: any) =>
                employeeExam?.idExamen === selectedEmployee?.examenImportacion?.id
        );

        return previousEmployeesExams;
    };
    return (
        <>
            <Box
                display="flex"
                justifyContent="space-between"
                padding={1}
                sx={{ flexDirection: 'column', height: '100%' }}
            >
                <Grid>
                    <DrawerHeader
                        onIconClick={handleCloseDrawer}
                        text={`${selectedEmployee?.nombre || ''} - ${
                            selectedEmployee?.riesgo?.codigo || ''
                        }  ${selectedEmployee?.riesgo?.descripcion || ''}`}
                    />

                    {examOptions.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
                            <Typography variant="body2" fontStyle="italic">
                                {translate('reconfirmation.drawer.assignExam.noPreviousExams')}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="subtitle1" fontSize={'1.43rem'} mt={3} pb={2}>
                                {t('reconfirmation.drawer.assignExam.previousExam')}
                            </Typography>
                            <Grid container spacing={2}>
                                {adaptedExamsData()?.map((exam: any) => (
                                    <>
                                        <Grid item xs={12} key={exam?.examen?.id}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: '8px',
                                                    backgroundColor: '#E0EBED',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant="body1" fontWeight="bold">
                                                        {exam?.examenMedico?.descripcion}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <Box component="span" fontWeight="bold">
                                                            Frecuencia:
                                                        </Box>{' '}
                                                        {exam.examenMedico?.frecuencia}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <Box component="span" fontWeight="bold">
                                                            Cantidad:
                                                        </Box>{' '}
                                                        {exam?.cantidad}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            {renderDownloadButton('Descargar resultado', () =>
                                                handleDownloadPdf(exam.idExamen)
                                            )}
                                        </Grid>
                                    </>
                                ))}
                            </Grid>
                        </>
                    )}
                    <Grid container spacing={0} mt={2}>
                        <Typography variant="subtitle1" fontSize={'1.43rem'} mt={3}>
                            {t('reconfirmation.drawer.assignExam.assignNewExams')}
                        </Typography>

                        {fieldsConfig.map((field, index) => (
                            <Grid
                                item
                                xs={field?.gridSpace?.xs}
                                sm={field?.gridSpace?.sm}
                                md={field?.gridSpace?.md}
                                lg={field?.gridSpace?.lg}
                                key={index}
                            >
                                <CustomField
                                    config={field}
                                    value={
                                        formData[
                                            field.name as keyof ReconfirmationExamDrawerFormData
                                        ]
                                    }
                                    onChange={handleChange}
                                    error={errors[field.name as string]?.hasError}
                                    helperText={
                                        errors[field.name as string]?.errorMessage ||
                                        field.helperText
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                <Grid
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    sx={{ marginTop: '1rem' }}
                >
                    <Box
                        sx={{
                            '& > :not(style)': { m: 1 },
                            display: 'flex'
                        }}
                    >
                        <ButtonActions
                            handleClose={handleCloseDrawer}
                            renderBackAction
                            returnText={translate(
                                'reconfirmation.drawer.assignExam.buttons.cancel'
                            )}
                            confirmText={translate('reconfirmation.drawer.assignExam.buttons.save')}
                            loading={false}
                            disabled={isAssigningExam}
                            onClick={() => handleSave(selectedEmployee)}
                            borderRadius="4px"
                            size="small"
                        />
                    </Box>
                </Grid>
                <BackdropComponent
                    loading={
                        isLoading ||
                        isFetchingExamenesEmpleadoData ||
                        isAssigningExam ||
                        downloading
                    }
                />
            </Box>
        </>
    );
};

export default PeriodicosReconfirmationExamDrawer;
